import React, { useMemo, useState } from 'react';
import { Collapse, Input, Alert, Icon } from 'antd';
import { get, set, unset, cloneDeep } from 'lodash';
import { SETTINGS_SCHEMA, CATEGORIES, getSchemaKeys } from './settingsSchema';
import { SettingField } from './SettingField';
import { MetrcCredsCard } from './MetrcCredsCard';

const { Panel } = Collapse;

const collectLeafPaths = (obj, prefix = '', schemaKeys) => {
  const paths = [];
  for (const key in obj) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const val = obj[key];
    if (schemaKeys.has(fullPath)) {
      paths.push(fullPath);
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      paths.push(...collectLeafPaths(val, fullPath, schemaKeys));
    } else {
      paths.push(fullPath);
    }
  }
  return paths;
};

// Build a synthetic schema for a key that isn't in the registry so it can be
// edited with the same controls as registered settings. The control type is
// inferred from the current value.
const inferUnknownSchema = (key, val) => {
  let type = 'json';
  if (typeof val === 'boolean') type = 'boolean';
  else if (typeof val === 'number') type = 'number';
  else if (typeof val === 'string') type = 'string';
  else if (Array.isArray(val) && val.every(v => typeof v === 'string')) type = 'stringArray';
  return {
    key,
    type,
    label: key,
    description: 'Not in the settings registry — value is edited as-is.',
    category: 'Other',
    isUnknown: true,
  };
};

const cleanEmptyParents = (obj, path) => {
  const parts = path.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const parentPath = parts.slice(0, i).join('.');
    const parent = get(obj, parentPath);
    if (parent && typeof parent === 'object' && Object.keys(parent).length === 0) {
      unset(obj, parentPath);
    } else {
      break;
    }
  }
};

export const SettingsForm = ({ value, onChange }) => {
  const [search, setSearch] = useState('');
  const schemaKeys = useMemo(() => getSchemaKeys(), []);

  const handleFieldChange = (key, newValue) => {
    const updated = cloneDeep(value || {});
    if (newValue === undefined) {
      unset(updated, key);
      cleanEmptyParents(updated, key);
    } else {
      set(updated, key, newValue);
    }
    onChange(updated);
  };

  const filteredSchema = useMemo(() => {
    if (!search.trim()) return SETTINGS_SCHEMA;
    const term = search.toLowerCase();
    return SETTINGS_SCHEMA.filter(s =>
      s.label.toLowerCase().includes(term) ||
      s.key.toLowerCase().includes(term) ||
      s.description.toLowerCase().includes(term) ||
      s.category.toLowerCase().includes(term)
    );
  }, [search]);

  const settingsByCategory = useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => { map[c] = []; });
    filteredSchema.forEach(s => {
      if (map[s.category]) map[s.category].push(s);
    });
    return map;
  }, [filteredSchema]);

  const unknownKeys = useMemo(() => {
    if (!value) return [];
    const allPaths = collectLeafPaths(value, '', schemaKeys);
    return allPaths.filter(p => !schemaKeys.has(p));
  }, [value, schemaKeys]);

  const activeCategories = CATEGORIES.filter(c => settingsByCategory[c] && settingsByCategory[c].length > 0);

  return (
    <div style={{ textAlign: 'left' }}>
      <Input.Search
        placeholder="Search settings by name, key, or description..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />

      <Collapse defaultActiveKey={[]} bordered={false}>
        {activeCategories.map(category => (
          <Panel
            header={<span style={{ fontWeight: 600, fontSize: 14 }}>{category}</span>}
            key={category}
          >
            {category === 'License' && <MetrcCredsCard />}
            {settingsByCategory[category].map(schema => (
              <SettingField
                key={schema.key}
                schema={schema}
                value={get(value, schema.key)}
                onChange={val => handleFieldChange(schema.key, val)}
              />
            ))}
          </Panel>
        ))}

        {unknownKeys.length > 0 && (
          <Panel
            header={
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                <Icon type="setting" style={{ marginRight: 8 }} />
                Other Settings ({unknownKeys.length})
              </span>
            }
            key="__unknown__"
          >
            <Alert
              type="info"
              showIcon
              message="These settings exist in the configuration but aren't in the settings registry, so they appear here automatically. They may have been added manually via the Raw JSON editor, or are used by server-side code. They are fully editable — the input type is inferred from the current value. Keys starting with 'menu.' control sidebar menu item visibility per route."
              style={{ marginBottom: 16 }}
            />
            {unknownKeys.map(key => {
              const val = get(value, key);
              return (
                <SettingField
                  key={key}
                  schema={inferUnknownSchema(key, val)}
                  value={val}
                  onChange={newVal => handleFieldChange(key, newVal)}
                />
              );
            })}
          </Panel>
        )}
      </Collapse>
    </div>
  );
};
